import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import StaffPinModal from './staff/StaffPinModal';

const DEFAULT_IDLE_TIMEOUT_MS = 60 * 1000;
const HOME_ROUTE = '/dashboard';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'];

const isStaffLoginEnabled = () => localStorage.getItem('staff_login_enabled') === '1';
// Independent of staff_login_enabled - a restaurant can keep PIN login on but turn the idle lock
// off. Both cached alongside staff_login_enabled so a lost connection falls back to last-known
// values rather than silently reverting to the hardcoded 60s default.
const isLockScreenEnabled = () => localStorage.getItem('lock_screen_enabled') !== '0';
const getLockScreenTimeoutMs = () => {
  const seconds = parseInt(localStorage.getItem('lock_screen_timeout_seconds'), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_IDLE_TIMEOUT_MS;
};

/**
 * Idle-timeout screen lock, and the one place that establishes who is working this till.
 *
 * Three things keep this from disturbing the existing estate:
 *
 *   1. It is opt-in per restaurant. `staff_login_enabled` comes from res_config, no restaurant has
 *      a row, and an absent row reads as off - so for every restaurant today this component
 *      mounts, asks once, and renders nothing ever again.
 *   2. It is not a mandatory login. Even when enabled, the idle timer is only armed while the user
 *      is sitting on the Home Screen, and only if the separate "Screen Lock on Idle" toggle is
 *      also on (length configurable in Settings, 60s by default). Deep inside an order or a
 *      booking, idling never locks the screen, because locking a till mid-order is worse than
 *      leaving it unlocked.
 *   3. It gates nothing. A successful PIN records who is active and what they may do; no screen
 *      consults that yet.
 *
 * Also the target of Header's "Switch User", which dispatches `staffSwitchRequested` so the prompt
 * can be opened from any route without prop drilling through the layout.
 */
const StaffPinGate = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const timerRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    clearTimeout(timerRef.current);
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (location.pathname !== HOME_ROUTE) {
      return undefined;
    }

    // Re-checked on every entry to the Home Screen rather than trusted from the value cached at
    // restaurant login, so switching a restaurant on or off doesn't require every till in the
    // building to log out and back in.
    (async () => {
      let enabled = isStaffLoginEnabled();
      let lockEnabled = isLockScreenEnabled();
      let timeoutMs = getLockScreenTimeoutMs();
      try {
        const response = await callAPI(apiUrls.STAFF_LOGIN_ENABLED, {}, 'GET');
        const data = response?.data?.data || {};
        enabled = !!data.staff_login_enabled;
        lockEnabled = data.lock_screen_enabled !== false;
        timeoutMs = (Number(data.lock_screen_timeout_seconds) || 60) * 1000;
        localStorage.setItem('staff_login_enabled', enabled ? '1' : '0');
        localStorage.setItem('lock_screen_enabled', lockEnabled ? '1' : '0');
        localStorage.setItem('lock_screen_timeout_seconds', String(timeoutMs / 1000));
      } catch (e) {
        // Offline or the endpoint is unreachable - fall back to the last known value rather than
        // locking a till that might not even use this feature.
      }

      if (cancelled || !enabled || !lockEnabled) {
        return;
      }

      const resetTimer = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setIsOpen(true), timeoutMs);
      };

      ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer));
      resetTimer();

      cleanupRef.current = () => {
        clearTimeout(timerRef.current);
        ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      };
    })();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const onStaffSwitchRequested = () => {
      setError('');
      setIsOpen(true);
    };
    window.addEventListener('staffSwitchRequested', onStaffSwitchRequested);
    return () => window.removeEventListener('staffSwitchRequested', onStaffSwitchRequested);
  }, []);

  const handleConfirm = async (pin) => {
    setBusy(true);
    setError('');
    try {
      const response = await callAPI(apiUrls.STAFF_AUTH_VALIDATE_PIN, {}, 'POST', { pin });
      const data = response.data?.data;
      if (!data) {
        setError('PIN not recognised.');
        return;
      }

      // The permission list is the server's computed effective set (role template plus per-staff
      // overrides). Stored as-is and never derived from the role name, so an admin editing a role
      // takes effect on the next PIN entry without a POS release.
      localStorage.setItem('active_staff_id', data.employee_id);
      localStorage.setItem('active_staff_name', `${data.first_name || ''} ${data.last_name || ''}`.trim());
      localStorage.setItem('active_staff_role', data.role || '');
      localStorage.setItem('active_staff_permissions', JSON.stringify(data.permissions || []));
      window.dispatchEvent(new Event('staffPinValidated'));
      setIsOpen(false);
    } catch (e) {
      // validate-pin answers a wrong PIN with 422, never 401: the axios wrapper treats any 401 as
      // an expired restaurant token and tries to re-login the whole session, which a mistyped
      // staff PIN must not trigger.
      setError('PIN not recognised.');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <StaffPinModal
      isOpen={true}
      allowClose={false}
      onCancel={() => {}}
      onConfirm={handleConfirm}
      title="Staff Login"
      subtitle="Enter your PIN to continue"
      error={error}
      busy={busy}
    />
  );
};

export default StaffPinGate;
